import { c as convertToNumber } from './number-400b3bf2.js';

/**
 * A rule that checks if the input is less than or equal to a maximum value
 *
 * @implements {FormFieldValidationRule<string>}
 * @property {{ maxValue: number }} config config including the maxValue limit for validation
 */
class MaxNumberRule {
  /**
   * It creates a new instance of the class with the given config.
   *
   * @param {{ maxValue: number }} config config including the maxValue limit for validation
   * @param {number} config.maxValue number of the maxValue
   */
  constructor(config) {
    this.config = config;
  }
  /**
   * If the input is a number, return true if it's less than or equal to the maximum value, false otherwise
   *
   * @param {unknown} input - The input value to be evaluated.
   * @returns {EvaluateResult} The result of the evaluation.
   * @throws {Error} if input is unable to evaluate
   */
  evaluate(input) {
    var _a;
    if (!this.isAcceptedInputType(input)) {
      throw new Error(`Unexpected type of input: ${typeof input}: ${input}`);
    }
    const maxValue = ((_a = this.config) === null || _a === void 0 ? void 0 : _a.maxValue) || Number.MAX_VALUE;
    return {
      isValid: convertToNumber(input) <= maxValue,
      message: convertToNumber(input) <= maxValue ? '' : `The maximum input value is ${maxValue}`,
    };
  }
  /**
   * If the input is accepted and the status is not disabled and visible, then return true
   *
   * @param {unknown} input - The input to be evaluated.
   * @param {FormFieldStatusMeta} [status] - FormFieldStatusMeta
   * @returns {boolean} A boolean value.
   */
  canEvaluate(input, status) {
    return status
      ? this.isAcceptedInputType(input) && !status.disabled && status.visible
      : this.isAcceptedInputType(input);
  }
  /**
   * If the field is not disabled, visible, and has been touched or modified, then show the error
   *
   * @param {FormFieldStatusMeta} status - FormFieldStatusMeta
   * @returns {boolean} A boolean value.
   */
  shouldShowError(status) {
    return !status.disabled && status.visible && (status.touched || status.modified);
  }
  /**
   * If the input is not a number, then return false, otherwise return true.
   *
   * @param {unknown} input - The input value to be converted.
   * @returns {boolean} a boolean value.
   */
  isAcceptedInputType(input) {
    return !isNaN(convertToNumber(input));
  }
}

/**
 * A rule that checks if the input is larger than or equal to a minimum value
 *
 * @implements {FormFieldValidationRule<string>}
 * @property {{ minValue: number }} config config including the minValue limit for validation
 */
class MinNumberRule {
  /**
   * It creates a new instance of the class with the given config.
   *
   * @param {{ minValue: number }} config config including the minValue limit for validation
   * @param {number} config.minValue number of the minValue
   */
  constructor(config) {
    this.config = config;
  }
  /**
   * If the input is a number, return true if it's more than or equal to the minimum value, false otherwise
   *
   * @param {unknown} input - The input value to be evaluated.
   * @returns {EvaluateResult} The result of the evaluation.
   * @throws {Error} if input is unable to evaluate
   */
  evaluate(input) {
    var _a;
    if (!this.isAcceptedInputType(input)) {
      throw new Error(`Unexpected type of input: ${typeof input}: ${input}`);
    }
    const minValue = ((_a = this.config) === null || _a === void 0 ? void 0 : _a.minValue) || 0;
    return {
      isValid: convertToNumber(input) >= minValue,
      message: convertToNumber(input) >= minValue ? '' : `The minimum input value is ${minValue}`,
    };
  }
  /**
   * If the input is accepted and the status is not disabled and visible, then return true
   *
   * @param {unknown} input - The input to be evaluated.
   * @param {FormFieldStatusMeta} [status] - FormFieldStatusMeta
   * @returns {boolean} A boolean value.
   */
  canEvaluate(input, status) {
    return status
      ? this.isAcceptedInputType(input) && !status.disabled && status.visible
      : this.isAcceptedInputType(input);
  }
  /**
   * If the field is not disabled, visible, and has been touched or modified, then show the error
   *
   * @param {FormFieldStatusMeta} status - FormFieldStatusMeta
   * @returns {boolean} A boolean value.
   */
  shouldShowError(status) {
    return !status.disabled && status.visible && (status.touched || status.modified);
  }
  /**
   * If the input is not a number, then return false, otherwise return true.
   *
   * @param {unknown} input - The input value to be converted.
   * @returns {boolean} a boolean value.
   */
  isAcceptedInputType(input) {
    return !isNaN(convertToNumber(input));
  }
}

export { MinNumberRule as M, MaxNumberRule as a };
